import { Checkbox, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import { PropertyDisplayFormat, TableColumnAlign, TableColumnModel } from '../../Models/TableColumnModel';
import { SortableCollapsibleItem } from './SortableCollapsibleItem';
import { SortableItemListDialog } from './SortableItemListDialog';

const FORMAT_OPTIONS: { value: PropertyDisplayFormat; label: string }[] = [
	{ value: 'Date', label: 'Date' },
	{ value: 'DateTime', label: 'Date & Time' },
	{ value: 'Currency', label: 'Currency' },
	{ value: 'Link', label: 'Link' },
	{ value: 'PhoneLink', label: 'Phone Link' },
	{ value: 'EmailLink', label: 'Email Link' },
];

const ALIGN_OPTIONS: { value: TableColumnAlign; label: string }[] = [
	{ value: 'Left', label: 'Left' },
	{ value: 'Center', label: 'Center' },
	{ value: 'Right', label: 'Right' },
];

// ---- Column row content ----

function parseOptionalNumber(value: string): number | undefined {
	const trimmedValue = value.trim();
	if (trimmedValue.length === 0) {
		return undefined;
	}

	const parsedValue = Number(trimmedValue);
	return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

type ColumnEditorProps = {
	column: TableColumnModel;
	onChange: (updated: TableColumnModel) => void;
};

function ColumnEditor({ column, onChange }: ColumnEditorProps) {
	const hasFixedAndFlexWidth = column.Width !== undefined && column.Flex !== undefined;

	return (
		<>
			<Stack direction="row" spacing={2}>
				<TextField label="Label" value={column.Label} onChange={(e) => onChange({ ...column, Label: e.target.value })} size="small" required sx={{ flex: 1 }} />
				<TextField
					label="Field"
					value={column.Field ?? ''}
					onChange={(e) => onChange({ ...column, Field: e.target.value || undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
			</Stack>

			<Stack direction="row" spacing={2}>
				<FormControl size="small" sx={{ flex: 1 }}>
					<InputLabel>Format</InputLabel>
					<Select
						label="Format"
						value={column.Format ?? ''}
						onChange={(e) => onChange({ ...column, Format: (e.target.value as PropertyDisplayFormat) || undefined })}
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{FORMAT_OPTIONS.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ flex: 1 }}>
					<InputLabel>Align</InputLabel>
					<Select
						label="Align"
						value={column.Align ?? 'Left'}
						onChange={(e) => {
							const align = e.target.value as TableColumnAlign;
							onChange({ ...column, Align: align === 'Left' ? undefined : align });
						}}
					>
						{ALIGN_OPTIONS.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					label="Value Template"
					value={column.ValueTemplate ?? ''}
					onChange={(e) => onChange({ ...column, ValueTemplate: e.target.value || undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
			</Stack>

			<Stack direction="row" spacing={2}>
				<TextField
					label="Width"
					type="number"
					value={column.Width ?? ''}
					onChange={(e) => onChange({ ...column, Width: parseOptionalNumber(e.target.value) })}
					size="small"
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Min Width"
					type="number"
					value={column.MinWidth ?? ''}
					onChange={(e) => onChange({ ...column, MinWidth: parseOptionalNumber(e.target.value) })}
					size="small"
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Max Width"
					type="number"
					value={column.MaxWidth ?? ''}
					onChange={(e) => onChange({ ...column, MaxWidth: parseOptionalNumber(e.target.value) })}
					size="small"
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Flex"
					type="number"
					value={column.Flex ?? ''}
					onChange={(e) => onChange({ ...column, Flex: parseOptionalNumber(e.target.value) })}
					size="small"
					sx={{ flex: 1 }}
					helperText={hasFixedAndFlexWidth ? 'Flex can override fixed width in the table grid.' : undefined}
				/>
			</Stack>
			{hasFixedAndFlexWidth && (
				<FormHelperText>
					Use either Width for fixed sizing or Flex for proportional sizing unless you specifically want DataGrid to balance both constraints.
				</FormHelperText>
			)}

			<Stack direction="row" spacing={2} flexWrap="wrap">
				<FormControlLabel
					control={
						<Checkbox
							checked={column.IsActionsColumn ?? false}
							onChange={(e) => onChange({ ...column, IsActionsColumn: e.target.checked || undefined })}
							size="small"
						/>
					}
					label="Actions Column"
				/>
				<FormControlLabel
					control={
						<Checkbox checked={column.IsSortable ?? false} onChange={(e) => onChange({ ...column, IsSortable: e.target.checked || undefined })} size="small" />
					}
					label="Sortable"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={column.DefaultSortBy ?? false}
							onChange={(e) => onChange({ ...column, DefaultSortBy: e.target.checked || undefined })}
							size="small"
						/>
					}
					label="Default Sort"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={column.DefaultSortByAscending ?? true}
							onChange={(e) => onChange({ ...column, DefaultSortByAscending: e.target.checked })}
							size="small"
							disabled={!column.DefaultSortBy}
						/>
					}
					label="Ascending"
				/>
			</Stack>
		</>
	);
}

// ---- Dialog ----

type TableColumnsInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	columns: TableColumnModel[] | undefined;
	onColumnsChange: (columns: TableColumnModel[]) => void;
};

export function TableColumnsInputDialog({ open, setOpen, columns, onColumnsChange }: TableColumnsInputDialogProps) {
	return (
		<SortableItemListDialog
			open={open}
			setOpen={setOpen}
			title="Configure Columns"
			items={columns}
			onItemsChange={onColumnsChange}
			createItem={() => ({ Label: '' }) as TableColumnModel}
			addLabel="Add Column"
		>
			{(column, index, total, onChange, onMoveUp, onMoveDown, onDelete) => (
				<SortableCollapsibleItem
					key={index}
					title={column.Label || `Column ${index + 1}`}
					subtitle={column.Field}
					index={index}
					total={total}
					onMoveUp={onMoveUp}
					onMoveDown={onMoveDown}
					onDelete={onDelete}
				>
					<ColumnEditor column={column} onChange={onChange} />
				</SortableCollapsibleItem>
			)}
		</SortableItemListDialog>
	);
}
